/**
 * NavigationEquipment - Navigation equipment configuration section
 */

import React from 'react';
import { Checkbox } from '../../../../catalyst/checkbox';

const NavigationEquipment = ({ vesselConfig, updateConfig, mandatoryEquipment }) => {
  const handleOptionalEquipmentToggle = (equipment) => {
    const current = vesselConfig.optionalEquipment || [];
    const updated = current.includes(equipment)
      ? current.filter(e => e !== equipment)
      : [...current, equipment];
    
    // Create update object with optional equipment
    const updates = { optionalEquipment: updated };
    
    // Also set equipment flags when toggling specific equipment
    if (equipment === 'radar') {
      updates.hasRadar = !current.includes(equipment);
    }
    if (equipment === 'navtex') {
      updates.hasNavtex = !current.includes(equipment);
    }
    if (equipment === 'ais') {
      updates.hasAIS = !current.includes(equipment);
    }
    if (equipment === 'mf_radio') {
      updates.hasMF = !current.includes(equipment);
    }
    
    updateConfig(updates);
  };

  return (
    <div className="space-y-6">
      {/* Optional Navigation Equipment */}
      <div>
        <h3 className="text-base font-medium text-gray-900 mb-4">Optional Navigation Equipment</h3>
        <div className="space-y-4">
          {/* Radar - only show if not mandatory */}
          {!mandatoryEquipment?.includes('radar') && (
            <label className="flex items-start gap-3">
              <Checkbox
                checked={vesselConfig.optionalEquipment?.includes('radar') || vesselConfig.hasRadar || false}
                onChange={() => handleOptionalEquipmentToggle('radar')}
                className="mt-0.5"
              />
              <div>
                <p className="font-medium text-gray-900">Radar</p>
                <p className="text-sm text-gray-600">Marine radar system (optional for Cat {vesselConfig.category || '?'})</p>
              </div>
            </label>
          )}

          {/* NAVTEX - only show if not mandatory */}
          {!mandatoryEquipment?.includes('navtex') && (
            <label className="flex items-start gap-3">
              <Checkbox
                checked={vesselConfig.optionalEquipment?.includes('navtex') || vesselConfig.hasNavtex || false}
                onChange={() => handleOptionalEquipmentToggle('navtex')}
                className="mt-0.5"
              />
              <div>
                <p className="font-medium text-gray-900">NAVTEX Receiver</p>
                <p className="text-sm text-gray-600">Weather and navigation warnings receiver (optional for Cat {vesselConfig.category || '?'})</p>
              </div>
            </label>
          )}

          {/* AIS - only show if not mandatory */}
          {!mandatoryEquipment?.includes('ais') && (
            <label className="flex items-start gap-3">
              <Checkbox
                checked={vesselConfig.optionalEquipment?.includes('ais') || vesselConfig.hasAIS || false}
                onChange={() => handleOptionalEquipmentToggle('ais')}
                className="mt-0.5"
              />
              <div>
                <p className="font-medium text-gray-900">AIS (Automatic Identification System)</p>
                <p className="text-sm text-gray-600">Vessel tracking and collision avoidance (optional for this vessel)</p>
              </div>
            </label>
          )}

          {/* GPS Backup */}
          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('gps_backup') || false}
              onChange={() => handleOptionalEquipmentToggle('gps_backup')}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">GPS Backup Unit</p>
              <p className="text-sm text-gray-600">Secondary GPS for redundancy</p>
            </div>
          </label>

          {/* ECDIS */}
          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.hasECDIS || false}
              onChange={(checked) => updateConfig({ hasECDIS: checked })}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">ECDIS (Electronic Chart Display & Information System)</p>
              <p className="text-sm text-gray-600">IMO-approved electronic chart system - requires specific training</p>
            </div>
          </label>

          {/* Chart Plotter */}
          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('chart_plotter') || false}
              onChange={() => handleOptionalEquipmentToggle('chart_plotter')}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">Electronic Chart Plotter (Non-ECDIS)</p>
              <p className="text-sm text-gray-600">Electronic navigation charts display (not IMO approved)</p>
            </div>
          </label>

          {/* Autopilot */}
          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('autopilot') || false}
              onChange={() => handleOptionalEquipmentToggle('autopilot')}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">Autopilot</p>
              <p className="text-sm text-gray-600">Automatic steering system</p>
            </div>
          </label>

          {/* Gyro Compass */}
          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('gyro_compass') || false}
              onChange={() => handleOptionalEquipmentToggle('gyro_compass')}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">Gyro Compass</p>
              <p className="text-sm text-gray-600">Non-magnetic compass system</p>
            </div>
          </label>

          {/* Speed Log */}
          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('speed_log') || false}
              onChange={() => handleOptionalEquipmentToggle('speed_log')}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">Speed Log</p>
              <p className="text-sm text-gray-600">Speed through water measurement</p>
            </div>
          </label>

          {/* Night Vision */}
          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('night_vision') || false}
              onChange={() => handleOptionalEquipmentToggle('night_vision')}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">Night Vision/FLIR</p>
              <p className="text-sm text-gray-600">Thermal imaging camera</p>
            </div>
          </label>

          {/* Searchlight */}
          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('searchlight') || false}
              onChange={() => handleOptionalEquipmentToggle('searchlight')}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">Remote Searchlight</p>
              <p className="text-sm text-gray-600">Remote controlled searchlight</p>
            </div>
          </label>
        </div>
      </div>

      {/* Communication Equipment */}
      <div>
        <h3 className="text-base font-medium text-gray-900 mb-4">Communication Equipment</h3>
        <div className="space-y-4">
          {/* MF Radio - only show if not mandatory */}
          {!mandatoryEquipment?.includes('mf_radio') && (
            <label className="flex items-start gap-3">
              <Checkbox
                checked={vesselConfig.optionalEquipment?.includes('mf_radio') || vesselConfig.hasMF || false}
                onChange={() => handleOptionalEquipmentToggle('mf_radio')}
                className="mt-0.5"
              />
              <div>
                <p className="font-medium text-gray-900">MF/HF Radio</p>
                <p className="text-sm text-gray-600">Medium/High frequency radio (optional for Cat {vesselConfig.category || '?'})</p>
              </div>
            </label>
          )}

          {/* SSB Radio */}
          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('ssb_radio') || false}
              onChange={() => handleOptionalEquipmentToggle('ssb_radio')}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">SSB Radio</p>
              <p className="text-sm text-gray-600">Single sideband radio for long range</p>
            </div>
          </label>

          {/* Sat Phone */}
          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('sat_phone') || false}
              onChange={() => handleOptionalEquipmentToggle('sat_phone')}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">Satellite Phone</p>
              <p className="text-sm text-gray-600">Satellite communication system</p>
            </div>
          </label>

          {/* Internet */}
          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('internet') || false}
              onChange={() => handleOptionalEquipmentToggle('internet')}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">Internet/4G/5G</p>
              <p className="text-sm text-gray-600">Internet connectivity system</p>
            </div>
          </label>

          {/* Intercom */}
          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('intercom') || false}
              onChange={() => handleOptionalEquipmentToggle('intercom')}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">Intercom System</p>
              <p className="text-sm text-gray-600">Internal communication system</p>
            </div>
          </label>

          {/* PA System - May be auto-required */}
          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('pa_system') || false}
              onChange={() => handleOptionalEquipmentToggle('pa_system')}
              disabled={vesselConfig.autoRequiredPASystem}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">
                PA System
                {vesselConfig.autoRequiredPASystem && (
                  <span className="ml-2 text-xs text-blue-600">(Auto-required)</span>
                )}
              </p>
              <p className="text-sm text-gray-600">Public address system</p>
            </div>
          </label>
        </div>
      </div>
    </div>
  );
};

export default NavigationEquipment;